import { useEffect, useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { useTheme } from '../hooks/useTheme'
import { settingsApi, adminUtilsApi } from '../services/api'

const SELLER_PANELS = [
  { key: 'seller.sales_trend', label: 'Tendencia de Ventas' },
  { key: 'seller.sales_by_product', label: 'Ventas por Producto' },
  { key: 'seller.top_clients', label: 'Top 5 Clientes' },
  { key: 'seller.sales_by_district', label: 'Ventas por Distrito' },
  { key: 'seller.collections', label: 'Cartera de Cobranza' },
  { key: 'seller.inactive_customers', label: 'Clientes Inactivos' },
  { key: 'seller.recent_sales', label: 'Ventas Recientes' },
]

const ADMIN_PANELS = [
  { key: 'admin.sales_trend', label: 'Tendencia de Ventas' },
  { key: 'admin.revenue_by_product', label: 'Ingresos por Producto' },
  { key: 'admin.cash_flow_trend', label: 'Tendencia de Caja' },
  { key: 'admin.customers_trend', label: 'Tendencia de Registro de Clientes' },
  { key: 'admin.orders_trend', label: 'Tendencia de Pedidos' },
  { key: 'admin.top_sales', label: 'Top 10 Ventas' },
  { key: 'admin.customers_by_business_type', label: 'Clientes por Tipo de Negocio' },
  { key: 'admin.revenue_by_district', label: 'Facturación por Distrito' },
  { key: 'admin.revenue_by_business_type', label: 'Facturación por Tipo de Negocio' },
  { key: 'admin.inventory_dio', label: 'Días Promedio en Etapa (DIO)' },
  { key: 'admin.inventory_turnover', label: 'Rotación Aproximada' },
  { key: 'admin.inventory_aging', label: 'Lotes en Envejecimiento' },
]

export default function SettingsPage() {
  const { user } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const [prefs, setPrefs] = useState<Record<string, boolean>>({})
  const [loading, setLoading] = useState(true)
  const [savingKey, setSavingKey] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [testEmailAddress, setTestEmailAddress] = useState('')
  const [testingEmail, setTestingEmail] = useState(false)
  const [testEmailResult, setTestEmailResult] = useState<{ ok: boolean; message: string } | null>(null)

  const panels = user?.role === 'admin' ? ADMIN_PANELS : SELLER_PANELS

  const handleTestEmail = async () => {
    setTestingEmail(true)
    setTestEmailResult(null)
    try {
      const res = await adminUtilsApi.testEmail(testEmailAddress || undefined)
      setTestEmailResult({ ok: true, message: res.data.message })
    } catch (err: any) {
      setTestEmailResult({ ok: false, message: err.response?.data?.error || 'No se pudo enviar el correo de prueba' })
    } finally {
      setTestingEmail(false)
    }
  }

  useEffect(() => {
    settingsApi
      .getDashboardPrefs()
      .then((res) => setPrefs(res.data.dashboard_prefs || {}))
      .catch(() => setError('No se pudieron cargar tus preferencias'))
      .finally(() => setLoading(false))
  }, [])

  const togglePanel = async (key: string) => {
    const newValue = !(prefs[key] !== false)
    setSavingKey(key)
    setError('')
    try {
      const res = await settingsApi.updateDashboardPrefs({ [key]: newValue })
      setPrefs(res.data.dashboard_prefs || {})
    } catch {
      setError('No se pudo guardar el cambio')
    } finally {
      setSavingKey(null)
    }
  }

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-espresso-800 mb-6">Configuración</h1>

      <div className="bg-cream-50 shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-espresso-800 mb-1">Paneles del Dashboard</h2>
        <p className="text-sm text-espresso-500 mb-4">
          Elige qué gráficos quieres ver en tu panel principal. Solo afecta tu propia vista.
        </p>
        {error && (
          <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm mb-4">{error}</div>
        )}
        <div className="space-y-3">
          {panels.map((p) => {
            const checked = prefs[p.key] !== false
            return (
              <label
                key={p.key}
                className="flex items-center justify-between border border-espresso-200 rounded-md px-4 py-3 cursor-pointer hover:bg-cream-50"
              >
                <span className="text-sm text-espresso-800">{p.label}</span>
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={savingKey === p.key}
                  onChange={() => togglePanel(p.key)}
                  className="h-4 w-4 text-terracotta-600 rounded border-espresso-300 focus:ring-terracotta-500"
                />
              </label>
            )
          })}
        </div>
      </div>

      <div className="bg-cream-50 shadow rounded-lg p-6 mt-6">
        <h2 className="text-lg font-medium text-espresso-800 mb-1">Apariencia</h2>
        <p className="text-sm text-espresso-500 mb-4">
          Elige cómo se ve la aplicación en este dispositivo. Se guarda en tu cuenta, así que
          también aplica si entras desde otro navegador o celular.
        </p>
        <label className="flex items-center justify-between border border-espresso-200 rounded-md px-4 py-3 cursor-pointer hover:bg-cream-100">
          <span className="text-sm text-espresso-800">Modo oscuro</span>
          <input
            type="checkbox"
            checked={theme === 'dark'}
            onChange={toggleTheme}
            className="h-4 w-4 text-terracotta-600 rounded border-espresso-300 focus:ring-terracotta-500"
          />
        </label>
      </div>

      {user?.role === 'admin' && (
        <div className="bg-cream-50 shadow rounded-lg p-6 mt-6">
          <h2 className="text-lg font-medium text-espresso-800 mb-1">Correo (SMTP)</h2>
          <p className="text-sm text-espresso-500 mb-4">
            Envía un correo de prueba para confirmar que la recuperación de contraseña y las
            exportaciones por correo funcionan. Si falla, el error exacto aparece abajo -- no hace
            falta revisar los logs del servidor.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="email"
              value={testEmailAddress}
              onChange={(e) => setTestEmailAddress(e.target.value)}
              placeholder={user?.email || 'correo@ejemplo.com'}
              className="flex-1 min-w-[200px] border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
            />
            <button
              type="button"
              onClick={handleTestEmail}
              disabled={testingEmail}
              className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-terracotta-600 hover:bg-terracotta-700 disabled:opacity-50"
            >
              {testingEmail ? 'Enviando...' : 'Enviar correo de prueba'}
            </button>
          </div>
          {testEmailResult && (
            <div
              className={`mt-3 px-4 py-2 rounded text-sm ${
                testEmailResult.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'
              }`}
              role="status"
            >
              {testEmailResult.message}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
